import Spinner from '@atlaskit/spinner';
import Tabs from '@atlaskit/tabs';
import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import styled from 'styled-components';

import PageContentLoaded from '../../apdex/PageContentLoaded';
import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import { PageContent } from '../../model/PageContent';
import ActivityStore from '../../store/activityStore';
import FeatureFlagsStore from '../../store/featureFlagsStore';
import SearchStore from '../../store/searchStore';
import Search from '../Search';

import Viewed from './Viewed';
import WorkedOn from './WorkedOn';

const ScreenCenteredSpinnerWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Header = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const TabsContainer = styled.div`
  margin: 0 -8px;
`;

const YourWorkTabsContainer = styled.div`
  position: relative;
  margin-top: 32px;
`;

interface TabData {
  analyticsName: string;
  content: JSX.Element;
  label: JSX.Element;
}

interface Props {
  activityStore: ActivityStore;
  featureFlagsStore: FeatureFlagsStore;
  searchStore: SearchStore;
}

interface State {
  selectedTab?: TabData;
}

@observer
class YourWorkTabs extends React.Component<Props & InjectedIntlProps & AnalyticsConsumerProps, State> {
  state = {
    selectedTab: undefined,
  };
  tabs: TabData[] = this.initTabs();

  componentDidMount() {
    when(
      () => this.props.featureFlagsStore.hasLoaded,
      () => {
        const { flags } = this.props.featureFlagsStore;
        this.props.activityStore.fetchActivity(flags['start.use-api-v-2']);
      },
    );
  }

  render() {
    const { activityStore, searchStore } = this.props;
    const emptyViews = activityStore.views.length === 0;
    const emptyWorks = activityStore.works.length === 0;

    if (!activityStore.hasLoaded) {
      return (
        <ScreenCenteredSpinnerWrapper>
          <Spinner size="medium" />
        </ScreenCenteredSpinnerWrapper>
      );
    }

    if (emptyViews && emptyWorks) {
      return <PageContentLoaded emptyState pageContent={PageContent.ACTIVITY} />;
    }

    const shouldHideMyWorkTabs = searchStore.isSearching && (searchStore.isLoading || searchStore.hasLoaded);

    return (
      <YourWorkTabsContainer>
        <PageContentLoaded pageContent={PageContent.ACTIVITY} />
        <Header>{this.props.intl.formatMessage(messages.yourWork)}</Header>
        <Search />
        {!shouldHideMyWorkTabs && (
          <TabsContainer>
            <Tabs onSelect={this.selectTab} selected={this.getSelectedTab()} tabs={this.tabs} />
          </TabsContainer>
        )}
      </YourWorkTabsContainer>
    );
  }

  private getSelectedTab(): TabData {
    if (this.state.selectedTab) {
      return this.state.selectedTab!;
    }
    if (this.props.activityStore.works.length === 0) {
      return this.tabs[1]; // Views
    } else {
      return this.tabs[0]; // default to first tab (Works)
    }
  }

  private initTabs(): TabData[] {
    const { intl } = this.props;
    return [
      {
        analyticsName: 'tabWorkedOn',
        content: <WorkedOn />,
        label: <span data-test-id="workedOnTab">{intl.formatMessage(messages.workedOn)}</span>,
      },
      {
        analyticsName: 'tabViewed',
        content: <Viewed />,
        label: <span data-test-id="viewedTab">{intl.formatMessage(messages.viewed)}</span>,
      },
    ];
  }

  private selectTab = (tab: TabData) => {
    void this.props.analytics.ui(tab.analyticsName, 'clicked');
    this.setState({
      selectedTab: tab,
    });
  };
}

export default inject(({ activityStore, featureFlagsStore, searchStore }) => ({
  activityStore,
  featureFlagsStore,
  searchStore,
}))(injectIntl(withAnalytics(YourWorkTabs)));

const messages = defineMessages({
  viewed: {
    defaultMessage: 'Viewed',
    description: 'Tab of recently viewed pages/issues',
    id: 'uchi.yourWork.recentlyViewed',
  },
  workedOn: {
    defaultMessage: 'Worked on',
    description: 'Tab of pages/issues the current user has worked on recently',
    id: 'uchi.yourWork.workedOn',
  },

  yourWork: {
    defaultMessage: 'Your recent work',
    description: 'title of page section containing work of current user',
    id: 'uchi.yourWork.title',
  },
});
